import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Sparkles, Loader2, Zap } from "lucide-react";

const fmt = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

export default function Planos() {
  const [data, setData] = useState(null);
  const [yearly, setYearly] = useState(false);
  const { user } = useAuth();
  const ctaTo = user ? "/assinatura" : "/cadastro";

  useEffect(() => {
    api.get("/plans").then((r) => setData(r.data)).catch(() => setData({ plans: [] }));
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <Loader2 className="w-8 h-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <MarketingNav />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold uppercase tracking-widest text-brand-purple shadow-sm">
            Planos EDUCARTE
          </span>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl font-black tracking-tight text-brand-ink">
            Escolha o plano ideal
          </h1>
          <p className="mt-4 text-brand-muted text-lg">
            Crie sua conta gratuitamente. Escolha e assine um plano quando quiser.
          </p>

          <div className="mt-8 inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1 shadow-sm">
            <button
              onClick={() => setYearly(false)}
              data-testid="toggle-mensal"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                !yearly ? "bg-brand-blue text-white" : "text-brand-muted"
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setYearly(true)}
              data-testid="toggle-anual"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                yearly ? "bg-brand-blue text-white" : "text-brand-muted"
              }`}
            >
              Anual <span className="text-brand-green">-16%</span>
            </button>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-6 lg:gap-6 items-start">
          {data.plans.map((plan, i) => {
            const price = yearly ? plan.price_yearly / 12 : plan.price_monthly;
            const popular = plan.popular;
            return (
              <motion.div
                key={plan.plan_id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                data-testid={`plan-card-${plan.plan_id}`}
                className={`relative bg-white rounded-3xl p-8 border transition-all ${
                  popular
                    ? "border-brand-purple ring-2 ring-brand-purple lg:scale-105 shadow-2xl shadow-purple-100 z-10"
                    : "border-slate-100 shadow-sm hover:shadow-lg"
                }`}
              >
                {popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-brand-purple text-white text-xs font-bold uppercase tracking-wider shadow-lg">
                    <Zap className="w-3.5 h-3.5" /> Mais popular
                  </span>
                )}
                <h3 className="font-heading font-extrabold text-xl text-brand-ink">{plan.name}</h3>
                <p className="mt-2 text-sm text-brand-muted min-h-[40px]">{plan.tagline}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-heading text-4xl font-black text-brand-ink">
                    {fmt(price)}
                  </span>
                  <span className="text-brand-muted mb-1.5 text-sm">/mês</span>
                </div>
                {yearly && (
                  <p className="mt-1 text-xs text-brand-green font-medium">
                    Cobrado {fmt(plan.price_yearly)} por ano
                  </p>
                )}
                <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue bg-blue-50 px-3 py-1 rounded-full">
                  Até {plan.max_players} participantes
                </div>
                <Link to={ctaTo}>
                  <Button
                    data-testid={`plan-cta-${plan.plan_id}`}
                    className={`mt-6 w-full rounded-full h-12 font-semibold hover:scale-[1.02] transition-transform ${
                      popular
                        ? "bg-brand-purple hover:bg-purple-700"
                        : "bg-brand-blue hover:bg-blue-700"
                    }`}
                  >
                    {user ? "Assinar agora" : "Criar conta grátis"}
                  </Button>
                </Link>
                <ul className="mt-7 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-brand-ink">
                      <Check className="w-4 h-4 text-brand-green mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* One-off quiz */}
        <div className="mt-12 max-w-3xl mx-auto bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-6 justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-brand-yellow" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-brand-ink">Quiz avulso</h3>
              <p className="text-sm text-brand-muted mt-1">
                Sem assinatura? Encomende a elaboração de um quiz completo com IA.
              </p>
            </div>
          </div>
          <div className="text-center sm:text-right">
            <p className="font-heading text-3xl font-black text-brand-ink">
              {fmt(data.single_quiz_price || 89.9)}
            </p>
            <span className="text-xs text-brand-muted">pagamento único</span>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
